import React, { useEffect } from "react"
import { useQuill } from "react-quilljs"
import _ from "lodash";
import Quill from "quill";
import 'quill/dist/quill.snow.css'; // Add css for snow theme
import styles from "./styles.module.scss"
import { getS3UploadUrl, uploadS3File } from "utils/s3";
import { toast } from "react-toastify";
import QuillResizeImage from "quill-resize-image";

// 이미지 리사이즈 모듈 등록 (한 번만)
Quill.register("modules/imageResize", QuillResizeImage);

// ─────────────────────────────────────────────────────────────
// 이미지 크기(width/height/style) 보존용 커스텀 포맷
// 기본 Quill image 포맷은 src/alt만 직렬화하므로, 리사이즈한 크기가
// Delta → HTML 라운드트립 시 사라진다. 아래 포맷은 이를 보존한다.
// ─────────────────────────────────────────────────────────────
const ImageAttrs = ['alt', 'width', 'height', 'style'];
const BaseImage = Quill.import('formats/image') as any;
class ImageWithSize extends BaseImage {
    static formats(domNode: HTMLElement) {
        return ImageAttrs.reduce((acc: any, attr) => {
            if (domNode.hasAttribute(attr)) acc[attr] = domNode.getAttribute(attr);
            return acc;
        }, {});
    }
    format(name: string, value: string) {
        if (ImageAttrs.indexOf(name) > -1) {
            if (value) (this as any).domNode.setAttribute(name, value);
            else (this as any).domNode.removeAttribute(name);
        } else {
            super.format(name, value);
        }
    }
}
Quill.register(ImageWithSize, true);

// Custom Link format to auto-add https:// prefix
const Link = Quill.import('formats/link') as any;
class CustomLink extends Link {
    static sanitize(url: string) {
        const sanitizedUrl = super.sanitize(url);
        // Add https:// prefix if no protocol is present
        if (sanitizedUrl && !sanitizedUrl.match(/^https?:\/\//i)) {
            return `https://${sanitizedUrl}`;
        }
        return sanitizedUrl;
    }
}
Quill.register('formats/link', CustomLink, true);

// 업로드 중 placeholder (SVG 인라인) — utf8 인코딩으로 한글 안전 처리
const LOADING_PLACEHOLDER = 'data:image/svg+xml;utf8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="56" viewBox="0 0 240 56">' +
    '<rect width="240" height="56" fill="#f6f8fa" rx="6"/>' +
    '<text x="120" y="34" text-anchor="middle" font-family="sans-serif" font-size="13" fill="#71717a">이미지 업로드 중...</text>' +
    '</svg>'
);

interface BasicQuillEditorProps {
    placeholder?: string;
    onQuillReady?: (quill: Quill) => void;
    onChange?: (data: any) => void;
    initialContents?: any;
    customStyles?: React.CSSProperties;
    /** 본문 사이 이미지 삽입 기능 활성화 (S3 업로드 후 URL 삽입) */
    imageUpload?: boolean;
}

const BasicQuillEditor: React.FC<BasicQuillEditorProps> = ({
    placeholder = " ",
    onQuillReady = () => {},
    onChange = () => {},
    initialContents = {} as any,
    customStyles = {},
    imageUpload = false,
}) => {
    const toolbarContainer = [
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [
            'bold',
            'italic',
            'underline',
            {
                'color': [
                    'red', 'orange', 'yellow', 'green', 'blue', 'violet', false
                ]
            },
            {
                'background': [
                    'red', 'orange', 'yellow', 'green', 'blue', 'violet', false
                ]
            }
        ],
        [{ 'list': 'bullet' }, { 'list': 'ordered' }],
        imageUpload ? ['link', 'image'] : ['link'],
    ];

    const { quill, quillRef } = useQuill({
        theme: 'snow',
        modules: {
            toolbar: { container: toolbarContainer },
            uploader: { handler: () => {} },
            ...(imageUpload ? {
                imageResize: {
                    modules: ['Resize', 'DisplaySize'],
                },
            } : {}),
        },
        placeholder,
    });

    // ── S3 업로드 ──
    const uploadImage = async (file: File): Promise<string | null> => {
        try {
            const signedUrl = await getS3UploadUrl(file);
            if (!signedUrl) throw new Error('Failed to get signed URL');
            const finalUrl = await uploadS3File(file, signedUrl);
            if (!finalUrl) throw new Error('Failed to upload');
            return finalUrl;
        } catch (e) {
            console.error('[QuillEditor] image upload failed:', e);
            toast.error('이미지 업로드에 실패했습니다.');
            return null;
        }
    };

    const insertImagesAtCursor = async (q: Quill, files: FileList | File[] | null) => {
        if (!files || files.length === 0) return;
        const fileArr = Array.from(files);
        const range = q.getSelection(true);
        let currentIndex = range?.index ?? q.getLength();
        for (const file of fileArr) {
            // 1) placeholder 삽입
            q.insertEmbed(currentIndex, 'image', LOADING_PLACEHOLDER);
            // 2) S3 업로드
            const url = await uploadImage(file);
            // 3) placeholder 제거 후 실제 URL 삽입
            q.deleteText(currentIndex, 1);
            if (url) {
                q.insertEmbed(currentIndex, 'image', url);
                q.setSelection(currentIndex + 1, 0);
                currentIndex++;
            }
        }
    };

    const handleImageToolbarClick = (q: Quill) => {
        const input = document.createElement('input');
        input.setAttribute('multiple', 'multiple');
        input.setAttribute('accept', 'image/*');
        input.setAttribute('type', 'file');
        input.click();
        input.onchange = () => {
            insertImagesAtCursor(q, input.files);
        };
    };

    useEffect(() => {
        if (_.isNil(quill)) {
            return;
        }
        quill.on('text-change', () => {
            onChange(quill.getContents());
        });

        if (imageUpload) {
            // toolbar image 버튼 → 파일 선택 → S3 업로드 → URL 삽입
            const toolBar: any = quill.getModule('toolbar');
            toolBar.addHandler('image', () => handleImageToolbarClick(quill));

            // 붙여넣기/드래그앤드롭 이미지도 동일 흐름
            const uploader: any = quill.getModule('uploader');
            uploader.options.handler = async (_range: any, files: File[]) => {
                await insertImagesAtCursor(quill, files);
            };
        }

        onQuillReady(quill);
        quill.setContents(initialContents);
    }, [quill]);

    useEffect(() => {
        if (_.isNil(quill)) {
            return;
        }
        quill.setContents(initialContents);
    }, [initialContents]);

    return (
        <div>
            <div className={`${styles.qlWrapper} ${styles.customSize} focus-within:ring-2 focus-within:ring-brand-700 rounded-md`}>
                <div ref={quillRef} style={customStyles} />
            </div>
            {imageUpload && (
                <p className="mt-1.5 text-xs text-gray-500">
                    이미지 모서리 드래그로 크기를 조절할 수 있습니다.
                    <span className="ml-1">비율을 유지하려면 <kbd className="px-1 py-0.5 rounded border border-gray-300 bg-gray-50 text-[10px] font-mono">Alt</kbd> 키를 누른 채로 드래그하세요.</span>
                </p>
            )}
        </div>
    );
};

export default BasicQuillEditor;
